import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { UserModel, CreateUserRequest, UpdateUserRequest } from '../models/UserModel';
import { 
  getUsers, 
  getUser, 
  createUser as createUserAPI, 
  updateUser as updateUserAPI, 
  deleteUser as deleteUserAPI 
} from './UserCRUD';

// Async thunks
export const fetchUsers = createAsyncThunk(
  'users/fetchUsers',
  async (params?: {
    search?: string;
    status?: string[];
    role?: string[];
    ordering?: string;
  }) => {
    const response = await getUsers(params);
    return response.data;
  }
);

export const fetchAllUsers = createAsyncThunk(
  'users/fetchAllUsers',
  async () => {
    const response = await getUsers();
    return response.data;
  }
);

export const fetchUserById = createAsyncThunk(
  'users/fetchUserById',
  async (id: string) => {
    const response = await getUser(id);
    return response.data;
  }
);

export const createUser = createAsyncThunk(
  'users/createUser',
  async (userData: CreateUserRequest) => {
    const response = await createUserAPI(userData);
    return response.data;
  }
);

export const updateUser = createAsyncThunk(
  'users/updateUser',
  async ({ id, userData }: { id: string; userData: UpdateUserRequest }) => {
    const response = await updateUserAPI(id, userData);
    return response.data;
  }
);

export const deleteUser = createAsyncThunk(
  'users/deleteUser',
  async (id: string) => {
    await deleteUserAPI(id);
    return id;
  }
);

// State interface
interface UserState {
  users: UserModel[];
  allUsers: UserModel[]; // Store all users for total count
  user: UserModel | null;
  loading: boolean;
  error: string | null;
}

// Initial state
const initialState: UserState = {
  users: [],
  allUsers: [],
  user: null,
  loading: false,
  error: null,
};

// Slice
const userSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearUser: (state) => {
      state.user = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch users
    builder
      .addCase(fetchUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.users = action.payload.results || action.payload || [];
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch users';
      })
      // Fetch all users (for total count)
      .addCase(fetchAllUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.allUsers = action.payload.results || action.payload || [];
      })
      .addCase(fetchAllUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch all users';
      })
      // Fetch user by ID
      .addCase(fetchUserById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserById.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
      })
      .addCase(fetchUserById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch user';
      })
      // Create user
      .addCase(createUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createUser.fulfilled, (state, action) => {
        state.loading = false;
        state.users.push(action.payload);
      })
      .addCase(createUser.rejected, (state, action) => {
        state.loading = false;
        // Handle validation errors from backend
        if (action.error.message?.includes('validation_error')) {
          try {
            const errorData = JSON.parse(action.error.message);
            if (errorData.type === 'validation_error' && errorData.errors) {
              const errorMessages = errorData.errors.map((err: any) => err.detail).join(', ');
              state.error = errorMessages;
            } else {
              state.error = action.error.message || 'Failed to create user';
            }
          } catch {
            state.error = action.error.message || 'Failed to create user';
          }
        } else {
          state.error = action.error.message || 'Failed to create user';
        }
      })
      // Update user
      .addCase(updateUser.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        
        // Optimistic update - apply changes immediately
        const { id, userData } = action.meta.arg;
        const userIndex = state.users.findIndex(user => user.id === id);
        if (userIndex !== -1) {
          const currentUser = state.users[userIndex];
          state.users[userIndex] = {
            ...currentUser,
            username: userData.username || currentUser.username,
            email: userData.email || currentUser.email,
            first_name: userData.first_name || currentUser.first_name,
            last_name: userData.last_name || currentUser.last_name,
            status: userData.status || currentUser.status,
            notes: userData.notes || currentUser.notes,
            // Note: role will be updated when backend responds
          };
        }
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.loading = false;
        // Replace with actual backend response
        const index = state.users.findIndex(user => user.id === action.payload.id);
        if (index !== -1) {
          state.users[index] = action.payload;
        }
        if (state.user && state.user.id === action.payload.id) {
          state.user = action.payload;
        }
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.loading = false;
        // Handle validation errors from backend
        if (action.error.message?.includes('validation_error')) {
          try {
            const errorData = JSON.parse(action.error.message);
            if (errorData.type === 'validation_error' && errorData.errors) {
              const errorMessages = errorData.errors.map((err: any) => err.detail).join(', ');
              state.error = errorMessages;
            } else {
              state.error = action.error.message || 'Failed to update user';
            }
          } catch {
            state.error = action.error.message || 'Failed to update user';
          }
        } else {
          state.error = action.error.message || 'Failed to update user';
        }
        
        // Revert optimistic update on error
        // Note: In a real app, you might want to show a toast notification
        // and let the user retry the operation
      })
      // Delete user
      .addCase(deleteUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.loading = false;
        state.users = state.users.filter(user => user.id !== action.payload);
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to delete user';
      });
  },
});

export const { clearError, clearUser } = userSlice.actions;
export default userSlice.reducer;
